import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import clsx from "clsx";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import styles from "./Modal.module.scss";
import { useSnackbar } from "notistack";
import recycledProductApi from "../../../apis/recycledProductApi";
import axiosAuth from "../../../apis/axiosAuth";
import axiosDonation from "../../../apis/axiosDonation";
import { API_URL_DONATION } from "../../../utils/setting";

import axios from "axios";

export interface ModalProps {
  className?: string;
  open: boolean;
  handleClose: () => void;
  itemEdit?: any;
  onSuccess: () => void;
}

type Inputs = {
  name: string;
  price: number;
  image: string;
  isActive: boolean;
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const ModalRecycledProduct = (props: ModalProps) => {
  const { className, open, handleClose, itemEdit, onSuccess } = props;
  const { register, handleSubmit, reset, setValue, watch } = useForm<Inputs>({
    defaultValues: {
      name: "",
      price: 0,
      image: "",
      isActive: true,
    }
  });

  const [isUploading, setIsUploading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (itemEdit) {
      setValue("name", itemEdit.name);
      setValue("price", itemEdit.price);
      setValue("image", itemEdit.image);
      setValue("isActive", itemEdit.isActive);
    } else {
      reset();
    }
  }, [itemEdit, setValue, reset]);


  const [openSubmitConfirm, setOpenSubmitConfirm] = useState(false);
  const [submitData, setSubmitData] = useState<Inputs | null>(null);

  const handleOpenSubmitConfirm = (data: Inputs) => {
    setSubmitData(data);
    setOpenSubmitConfirm(true);
  };

  const handleCloseSubmitConfirm = () => {
    setOpenSubmitConfirm(false);
    setSubmitData(null);
  };

  const onConfirmSubmit = async () => {
    if (!submitData) return;
    try {
      const payload = {
        name: submitData.name,
        price: Number(submitData.price),
        image: submitData.image,
        isActive: submitData.isActive,
      };

      let res;
      if (itemEdit) {
        res = await recycledProductApi.update({ ...payload, id: itemEdit.id });
      } else {
        res = await recycledProductApi.create(payload);
      }

      if (res.data.code === 0) {
        enqueueSnackbar(itemEdit ? "Cập nhật thành công" : "Thêm mới thành công", { variant: "success" });
        onSuccess();
      } else {
        enqueueSnackbar(res.data.message || "Lỗi", { variant: "error" });
      }
      handleCloseSubmitConfirm();
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Đã có lỗi xảy ra", { variant: "error" });
      handleCloseSubmitConfirm();
    }
  };

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    handleOpenSubmitConfirm(data);
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    try {
      // Gọi API Upload nội bộ của serene
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await axiosDonation.post(
        "/serene/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const relativeUrl = uploadRes.data?.data?.url;
      const baseUrl = (API_URL_DONATION || "").replace(/\/$/, "");
      const imageUrl = relativeUrl ? `${baseUrl}${relativeUrl}` : "";

      if (imageUrl) {
        setValue("image", imageUrl);
        enqueueSnackbar("Tải ảnh lên thành công", { variant: "success" });
      } else {
        throw new Error("Không lấy được đường dẫn ảnh sau khi upload");
      }

    } catch (error) {
      console.log(error);
      enqueueSnackbar("Lỗi khi tải ảnh lên server", { variant: "error" });
    } finally {
      setIsUploading(false);
    }
  };

  const isActiveWatch = watch("isActive");

  return (
    <div className={clsx(styles.root, className && className)}>
      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Typography variant="h6" component="h2">
            {itemEdit ? "Sửa sản phẩm" : "Thêm mới sản phẩm"}
          </Typography>

          <form className={styles.formWrap} onSubmit={handleSubmit(onSubmit)}>
            <TextField
              label="Tên sản phẩm"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: !!watch("name") ? true : undefined }}
              {...register("name", { required: true })}
            />

            <TextField
              label="Giá (VNĐ)"
              variant="outlined"
              type="number"
              fullWidth
              InputLabelProps={{ shrink: !!watch("price") || watch("price") === 0 ? true : undefined }}
              {...register("price", { required: true })}
            />

            <TextField
              label="Đường dẫn ảnh (URL)"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: !!watch("image") ? true : undefined }}
              {...register("image")}
            />

            <div className={styles.uploadWrap} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Button
                variant="outlined"
                component="label"
                disabled={isUploading}
              >
                {isUploading ? "Đang tải lên..." : "Chọn ảnh từ máy"}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleUploadImage}
                />
              </Button>
              {watch("image") && (
                <img src={watch("image")} alt="Preview" style={{ height: '40px', borderRadius: '4px' }} />
              )}
            </div>

            <FormControlLabel
              control={
                <Switch
                  checked={isActiveWatch}
                  onChange={(e) => setValue("isActive", e.target.checked)}
                />
              }
              label={isActiveWatch ? "Đang bật" : "Đã tắt"}
            />

            <Button type="submit" variant="contained" fullWidth disabled={isUploading}>
              {itemEdit ? "Cập nhật" : "Thêm mới"}
            </Button>
          </form>
        </Box>
      </Modal>

      <Dialog
        open={openSubmitConfirm}
        onClose={handleCloseSubmitConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Xác nhận lưu"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Bạn có chắc chắn muốn lưu thông tin sản phẩm này không?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmitConfirm} disabled={isUploading}>Hủy</Button>
          <Button onClick={onConfirmSubmit} autoFocus color="primary" disabled={isUploading}>
            Lưu
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ModalRecycledProduct;
